/**
 * HttpClient — the Connected-mode implementation of ApiClient.
 *
 * Talks to the Go orchestrator via cookie + CSRF auth. Every mutating
 * request carries the `X-CSRF-Token` header read from the `cp_csrf`
 * cookie the backend sets at signin.
 *
 * Base URL resolution:
 *   - Prod/staging: set via VITE_API_BASE_URL at build time (e.g.
 *     "https://checkout-api.costplus.io" — browser credentials cross-origin
 *     via CORS + `credentials: include`)
 *   - Local dev: empty string (same-origin — the designer proxies via
 *     Vite's dev server or the production reverse proxy)
 *
 * This is the ONLY file in the Studio codebase allowed to call `fetch`
 * directly. The ESLint rule `no-restricted-globals` allow-lists it via
 * the file-level override in eslint.config.js.
 */

import type {
  ApiClient,
  AnalyticsResource,
  AuthResource,
  CostplusProjectsResource,
  FixturesResource,
  FlowSidecar,
  FlowSidecarResource,
  FlowsResource,
  FlowVersionsResource,
  FraudProvidersResource,
  MediaResource,
  ShopifyResource,
  ShopifyStore,
  SyncProgress,
  SimulatorResource,
  TemplatesResource,
} from "./apiClient";
import type {
  Flow,
  FlowVersion,
  SimulatorResult,
} from "../types/flow";
import type {
  ABAnalyticsResponse,
  AddCostplusProjectInput,
  CostplusProject,
  CreateFlowFixtureInput,
  CreateFlowInput,
  CreateFlowVersionInput,
  FlowFixture,
  FlowTemplateCatalog,
  ForkFromTemplateInput,
  FraudProviderList,
  MediaBannerMetadata,
  PutFraudProviderInput,
  SimulateInput,
  UpdateFlowInput,
} from "../types/api";
import { ApiError } from "../types/api";

// ─── Config ───────────────────────────────────────────────────────────────────

export interface HttpClientConfig {
  /** Base URL, e.g. "https://checkout-api.costplus.io". Empty for same-origin. */
  baseUrl: string;
}

// ─── Low-level fetch wrapper ──────────────────────────────────────────────────

type FetchInit = {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  formData?: FormData;
  expectEmpty?: boolean;
};

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[-.]/g, "\\$&") + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

async function doFetch<T>(
  cfg: HttpClientConfig,
  path: string,
  init: FetchInit = {},
): Promise<T> {
  const url = new URL(cfg.baseUrl + path, window.location.origin);
  if (init.query) {
    for (const [k, v] of Object.entries(init.query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }

  const method = init.method ?? (init.body || init.formData ? "POST" : "GET");
  const headers: Record<string, string> = {};

  // CSRF for every mutating method. The backend sets `cp_csrf` at signin;
  // we read it here and double-submit the value.
  if (method !== "GET" && method !== "HEAD") {
    const csrf = readCookie("cp_csrf");
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  let body: FormData | string | undefined;
  if (init.formData) {
    body = init.formData;
  } else if (init.body !== undefined) {
    body = JSON.stringify(init.body);
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(url.toString(), {
    method,
    headers,
    body,
    credentials: "include",
  });

  // Empty 204 or 201-empty responses.
  if (init.expectEmpty || res.status === 204) {
    if (!res.ok) throw await toApiError(res);
    return undefined as T;
  }

  const text = await res.text();
  if (!res.ok) {
    throw toApiErrorFromText(res.status, text);
  }
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(res.status, {
      code: "PARSE_ERROR",
      message: "could not parse server response",
    });
  }
}

async function toApiError(res: Response): Promise<ApiError> {
  const text = await res.text();
  return toApiErrorFromText(res.status, text);
}

function toApiErrorFromText(status: number, text: string): ApiError {
  if (!text) {
    return new ApiError(status, { code: "HTTP_ERROR", message: `HTTP ${status}` });
  }
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === "object") {
      // Validation failures from the orchestrator's publish endpoint come
      // back as { errors: [{node_id, code, message}, ...] } (422). Roll
      // them into a single multi-line message the caller can render.
      if (Array.isArray(parsed.errors) && parsed.errors.length > 0) {
        const issues = parsed.errors as Array<{
          code?: string;
          message?: string;
          node_id?: string | null;
        }>;
        const message = issues
          .map((i) => i.message ?? i.code ?? "unknown")
          .filter(Boolean)
          .join("; ");
        const err = new ApiError(status, { code: "VALIDATION", message });
        (err as unknown as { issues: typeof issues }).issues = issues;
        return err;
      }
      if (typeof parsed.code === "string" && typeof parsed.message === "string") {
        return new ApiError(status, parsed);
      }
      if (typeof parsed.message === "string") {
        return new ApiError(status, { code: "HTTP_ERROR", message: parsed.message });
      }
    }
  } catch {
    /* fall through */
  }
  return new ApiError(status, { code: "HTTP_ERROR", message: text });
}

// ─── Resource builders ────────────────────────────────────────────────────────

function flowsResource(cfg: HttpClientConfig): FlowsResource {
  return {
    list: () =>
      doFetch<{ flows: Flow[] }>(cfg, "/v1/flows").then((r) => r.flows ?? []),
    create: (input: CreateFlowInput) => doFetch<Flow>(cfg, "/v1/flows", { body: input }),
    get: (id) => doFetch<Flow>(cfg, `/v1/flows/${id}`),
    update: (id, input: UpdateFlowInput) =>
      doFetch<Flow>(cfg, `/v1/flows/${id}`, { method: "PATCH", body: input }),
    delete: (id) =>
      doFetch<void>(cfg, `/v1/flows/${id}`, { method: "DELETE", expectEmpty: true }),
    duplicate: (id, input) =>
      doFetch<Flow>(cfg, `/v1/flows/${id}/duplicate`, { body: { name: input.name } }),
    syncFromSource: (flowId, input) =>
      doFetch(cfg, `/v1/flows/${flowId}/sync-source`, { method: "POST", body: input }),
  };
}

function versionsResource(cfg: HttpClientConfig): FlowVersionsResource {
  return {
    list: (flowId) =>
      doFetch<{ versions: FlowVersion[] }>(cfg, `/v1/flows/${flowId}/versions`).then(
        (r) => r.versions ?? [],
      ),
    create: (flowId, input: CreateFlowVersionInput) =>
      doFetch<FlowVersion>(cfg, `/v1/flows/${flowId}/versions`, { body: input }),
    get: (flowId, versionId) =>
      doFetch<FlowVersion>(cfg, `/v1/flows/${flowId}/versions/${versionId}`),
    publish: (flowId, versionId) =>
      doFetch<FlowVersion>(cfg, `/v1/flows/${flowId}/versions/${versionId}/publish`, {
        method: "POST",
      }),
    rollback: (flowId, versionId) =>
      doFetch<FlowVersion>(cfg, `/v1/flows/${flowId}/versions/${versionId}/rollback`, {
        method: "POST",
      }),
  };
}

function simulatorResource(cfg: HttpClientConfig): SimulatorResource {
  return {
    run: (flowId, input: SimulateInput) =>
      doFetch<SimulatorResult>(cfg, `/v1/flows/${flowId}/simulate`, { body: input }),
  };
}

function fixturesResource(cfg: HttpClientConfig): FixturesResource {
  return {
    list: (flowId) =>
      doFetch<{ fixtures: FlowFixture[] }>(cfg, `/v1/flows/${flowId}/fixtures`).then(
        (r) => r.fixtures ?? [],
      ),
    create: (flowId, input: CreateFlowFixtureInput) =>
      doFetch<FlowFixture>(cfg, `/v1/flows/${flowId}/fixtures`, { body: input }),
    get: async (flowId, fixtureId) => {
      const all = await doFetch<{ fixtures: FlowFixture[] }>(
        cfg,
        `/v1/flows/${flowId}/fixtures`,
      );
      const f = (all.fixtures ?? []).find((x) => x.id === fixtureId);
      if (!f) {
        throw new ApiError(404, {
          code: "FIXTURE_NOT_FOUND",
          message: "fixture not found",
        });
      }
      return f;
    },
    update: (flowId, fixtureId, input) =>
      doFetch<FlowFixture>(cfg, `/v1/flows/${flowId}/fixtures`, {
        body: { ...input, id: fixtureId },
      }),
    delete: (_flowId, fixtureId) =>
      doFetch<void>(cfg, `/v1/flows/fixtures/${fixtureId}`, {
        method: "DELETE",
        expectEmpty: true,
      }),
  };
}

function templatesResource(cfg: HttpClientConfig): TemplatesResource {
  return {
    catalog: () => doFetch<FlowTemplateCatalog>(cfg, "/v1/flow-templates"),
    fork: (input: ForkFromTemplateInput) =>
      doFetch<Flow>(cfg, "/v1/flows/from-template", { body: input }),
  };
}

function mediaResource(cfg: HttpClientConfig): MediaResource {
  return {
    uploadBanner: async (file, input) => {
      const form = new FormData();
      form.append("file", file);
      if (input.alt) form.append("alt", input.alt);
      return doFetch<MediaBannerMetadata>(cfg, "/v1/checkout/media/banners", {
        method: "POST",
        formData: form,
      });
    },
  };
}

function fraudProvidersResource(cfg: HttpClientConfig): FraudProvidersResource {
  return {
    list: () => doFetch<FraudProviderList>(cfg, "/v1/fraud-providers"),
    put: (provider, input: PutFraudProviderInput) =>
      doFetch<void>(cfg, `/v1/fraud-providers/${provider}`, {
        method: "PUT",
        body: input,
        expectEmpty: true,
      }),
    delete: (provider) =>
      doFetch<void>(cfg, `/v1/fraud-providers/${provider}`, {
        method: "DELETE",
        expectEmpty: true,
      }),
  };
}

function costplusProjectsResource(cfg: HttpClientConfig): CostplusProjectsResource {
  return {
    list: () =>
      doFetch<{ projects: CostplusProject[] }>(cfg, "/v1/costplus/projects").then(
        (r) => r.projects ?? [],
      ),
    add: (input: AddCostplusProjectInput) =>
      doFetch<CostplusProject>(cfg, "/v1/costplus/projects", { body: input }),
    update: (id, input) =>
      doFetch<CostplusProject>(cfg, `/v1/costplus/projects/${id}`, {
        method: "PATCH",
        body: input,
      }),
    verify: (id) =>
      doFetch<CostplusProject>(cfg, `/v1/costplus/projects/${id}/verify`, {
        method: "POST",
      }),
    remove: (id) =>
      doFetch<void>(cfg, `/v1/costplus/projects/${id}`, {
        method: "DELETE",
        expectEmpty: true,
      }),
  };
}

function authResource(cfg: HttpClientConfig): AuthResource {
  return {
    me: async () => {
      try {
        const m = await doFetch<{
          id: string;
          email: string;
          display_name: string | null;
        }>(cfg, "/v1/auth/me");
        return m;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    signin: async (input) => {
      await doFetch<void>(cfg, "/v1/auth/signin", {
        body: input,
        expectEmpty: true,
      });
    },
    signup: async (input) => {
      await doFetch<void>(cfg, "/v1/auth/signup", {
        body: input,
        expectEmpty: true,
      });
    },
    signout: async () => {
      await doFetch<void>(cfg, "/v1/auth/signout", {
        method: "POST",
        expectEmpty: true,
      });
    },
  };
}

function shopifyResource(cfg: HttpClientConfig): ShopifyResource {
  return {
    listStores: () =>
      doFetch<{ stores: ShopifyStore[] }>(cfg, "/v1/shopify/stores").then(
        (r) => r.stores ?? [],
      ),
    installURL: (shopDomain, returnTo) => {
      const q = new URLSearchParams({ shop: shopDomain });
      if (returnTo) q.set("return_to", returnTo);
      const base = cfg.baseUrl || "";
      return `${base}/v1/shopify/install?${q.toString()}`;
    },
    connect: (input) =>
      doFetch<{ install_url: string }>(cfg, "/v1/shopify/connect", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    disconnectStore: (shopDomain) =>
      doFetch<void>(cfg, `/v1/shopify/stores/${encodeURIComponent(shopDomain)}`, {
        method: "DELETE",
        expectEmpty: true,
      }),
    claimStore: (shopDomain, flowId) =>
      doFetch<void>(
        cfg,
        `/v1/shopify/stores/${encodeURIComponent(shopDomain)}/claim?flow_id=${encodeURIComponent(flowId)}`,
        { method: "POST", expectEmpty: true },
      ),
    resyncStore: (shopDomain) =>
      doFetch<void>(
        cfg,
        `/v1/shopify/stores/${encodeURIComponent(shopDomain)}/resync`,
        { method: "POST", expectEmpty: true },
      ),
    syncProgress: (shopDomain) =>
      doFetch<SyncProgress>(
        cfg,
        `/v1/shopify/stores/${encodeURIComponent(shopDomain)}/sync-progress`,
      ),
  };
}

function analyticsResource(cfg: HttpClientConfig): AnalyticsResource {
  return {
    abTestNode: (flowId, nodeId, range) =>
      doFetch<ABAnalyticsResponse>(
        cfg,
        `/v1/flows/${flowId}/nodes/${nodeId}/analytics`,
        { query: { from: range.from, to: range.to, currency: range.currency } },
      ),
  };
}

/**
 * Sidecar lives in the browser even in Connected mode — node positions are
 * a per-designer-session UX concern. Backend storage of sidecar data is on
 * the roadmap (one row per (merchant, flow) keyed by user); for now we
 * keep it in localStorage so it survives reloads in the same browser.
 */
function flowSidecarResource(_cfg: HttpClientConfig): FlowSidecarResource {
  const key = (flowId: string) => `costplus:flow-sidecar:${flowId}`;
  return {
    async get(flowId) {
      const raw = localStorage.getItem(key(flowId));
      if (!raw) return { positions: {} };
      try {
        return JSON.parse(raw) as FlowSidecar;
      } catch {
        return { positions: {} };
      }
    },
    async save(flowId, sidecar) {
      localStorage.setItem(key(flowId), JSON.stringify(sidecar));
    },
  };
}

// ─── Public factory ───────────────────────────────────────────────────────────

export function createHttpClient(cfg: HttpClientConfig): ApiClient {
  return {
    mode: "connected",
    auth: authResource(cfg),
    flows: flowsResource(cfg),
    flowSidecar: flowSidecarResource(cfg),
    versions: versionsResource(cfg),
    simulator: simulatorResource(cfg),
    fixtures: fixturesResource(cfg),
    templates: templatesResource(cfg),
    media: mediaResource(cfg),
    fraudProviders: fraudProvidersResource(cfg),
    costplusProjects: costplusProjectsResource(cfg),
    analytics: analyticsResource(cfg),
    shopify: shopifyResource(cfg),
  };
}
